import { AlertTriangle, Camera, RefreshCw, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMutation } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useGuardContext } from '@/hooks/useGuardContext';
import { useTranslation } from '@/hooks/useTranslation';
import { createGuardIncident, uploadIncidentPhoto } from '@/services/api.client';
import { extractApiErrorMessage } from '@/lib/api-error';
import type { IncidentResult } from '@/types';

const INCIDENT_TYPES = [
  { value: 'unauthorized_access', labelKey: 'guard.incident.typeUnauthorized' },
  { value: 'suspicious_activity', labelKey: 'guard.incident.typeSuspicious' },
  { value: 'property_damage', labelKey: 'guard.incident.typeDamage' },
  { value: 'medical_emergency', labelKey: 'guard.incident.typeMedical' },
  { value: 'theft', labelKey: 'guard.incident.typeTheft' },
  { value: 'disturbance', labelKey: 'guard.incident.typeDisturbance' },
  { value: 'other', labelKey: 'guard.incident.typeOther' },
] as const;

const MAX_PHOTOS = 3;
const MAX_PHOTO_SIZE = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];

/**
 * Incident Reporting — Guard incident form with photo upload.
 * Requirements: 5.1–5.5
 */
export function IncidentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isOnline } = useGuardContext();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [incidentType, setIncidentType] = useState('');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<IncidentResult | null>(null);

  const [submitError, setSubmitError] = useState<string | null>(null);

  const submitMutation = useMutation({
    mutationFn: createGuardIncident,
    onSuccess: (data: IncidentResult) => {
      setSubmitError(null);
      setResult(data);
    },
    onError: (err: unknown) => {
      setSubmitError(extractApiErrorMessage(err));
    },
  });

  const isOtherType = incidentType === 'other';
  const descriptionRequired = isOtherType;

  function handlePhotoAdd(e: React.ChangeEvent<HTMLInputElement>) {
    setPhotoError(null);
    const files = Array.from(e.target.files ?? []);
    for (const file of files) {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        setPhotoError(t('guard.incident.photoInvalidType'));
        return;
      }
      if (file.size > MAX_PHOTO_SIZE) {
        setPhotoError(t('guard.incident.photoTooLarge'));
        return;
      }
    }
    setPhotos((prev) => [...prev, ...files].slice(0, MAX_PHOTOS));
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function handlePhotoRemove(index: number) {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSubmit() {
    if (isUploading) return;
    let photoReference: string | undefined;
    if (photos.length > 0) {
      try {
        setIsUploading(true);
        const photo = photos[0];
        if (photo) {
          const { objectKey } = await uploadIncidentPhoto(photo);
          photoReference = objectKey;
        }
      } catch {
        // Photo upload failed — still submit the incident without photo
      } finally {
        setIsUploading(false);
      }
    }
    submitMutation.mutate({
      incidentType,
      description: description.trim() || incidentType.replace(/_/g, ' '),
      ...(photoReference ? { photoReference } : {}),
    });
  }

  const canSubmit =
    !!incidentType &&
    (!descriptionRequired || description.trim().length > 0) &&
    !submitMutation.isPending &&
    !isUploading;

  if (!isOnline) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl text-[var(--text-primary)]">{t('guard.incident.title')}</h1>
        <div className="rounded-[var(--radius-lg)] border border-[var(--status-amber)] bg-[var(--status-amber-tint)] p-6 text-center">
          <p className="text-sm text-[var(--status-amber-text)]">{t('guard.incident.offlineMessage')}</p>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="mx-auto max-w-md space-y-4">
        <h1 className="text-xl text-[var(--text-primary)]">{t('guard.incident.title')}</h1>
        <div className="space-y-4 rounded-[var(--radius-lg)] border-2 border-[var(--status-green)] bg-[var(--status-green-tint)] p-6 text-center">
          <AlertTriangle className="mx-auto size-10 text-[var(--status-green-text)]" />
          <h2 className="text-lg text-[var(--status-green-text)]">{t('guard.incident.submitted')}</h2>
          <p className="text-sm text-[var(--text-secondary)]">
            {t('guard.incident.reference')}: {result.referenceNumber}
          </p>
          <Button className="min-h-[44px]" onClick={() => navigate('/guard')}>
            {t('guard.incident.backToQueue')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <div className="flex items-center gap-3">
        <AlertTriangle className="size-6 text-[var(--status-red-text)]" />
        <h1 className="text-xl text-[var(--text-primary)]">{t('guard.incident.title')}</h1>
      </div>

      <div className="space-y-4 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--bg-surface)] p-6">
        <div className="space-y-2">
          <Label htmlFor="incident-type">{t('guard.incident.type')}</Label>
          <Select value={incidentType} onValueChange={setIncidentType}>
            <SelectTrigger id="incident-type" className="min-h-[44px]">
              <SelectValue placeholder={t('guard.incident.typePlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {INCIDENT_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {t(type.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="incident-description">
            {t('guard.incident.description')}
            {!descriptionRequired && (
              <span className="text-[var(--text-tertiary)]"> ({t('common.optional')})</span>
            )}
          </Label>
          <Textarea
            id="incident-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('guard.incident.descriptionPlaceholder')}
            rows={4}
          />
        </div>

        <div className="space-y-2">
          <Label>
            {t('guard.incident.photos')}{' '}
            <span className="text-[var(--text-tertiary)]">
              ({photos.length}/{MAX_PHOTOS})
            </span>
          </Label>

          {photos.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {photos.map((photo, i) => (
                <div
                  key={i}
                  className="relative size-20 rounded-[var(--radius-md)] border border-[var(--border-default)] overflow-hidden"
                >
                  <img
                    src={URL.createObjectURL(photo)}
                    alt={`${t('guard.incident.photo')} ${i + 1}`}
                    className="size-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => handlePhotoRemove(i)}
                    className="absolute top-0.5 right-0.5 flex size-5 items-center justify-center rounded-full bg-[var(--bg-page)]/60 text-white hover:bg-[var(--bg-page)]/80"
                    aria-label={`${t('common.delete')} ${t('guard.incident.photo')} ${i + 1}`}
                  >
                    <X className="size-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {photos.length < MAX_PHOTOS && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                onChange={handlePhotoAdd}
                className="hidden"
                id="photo-upload"
                aria-label={t('guard.incident.addPhoto')}
              />
              <Button
                variant="outline"
                size="sm"
                className="min-h-[44px]"
                onClick={() => fileInputRef.current?.click()}
              >
                <Camera className="mr-2 size-4" />
                {t('guard.incident.addPhoto')}
              </Button>
            </>
          )}

          {photoError && (
            <p className="text-xs text-[var(--text-error)]" role="alert">
              {photoError}
            </p>
          )}
        </div>

        {submitError && (
          <p className="text-sm text-[var(--text-error)]" role="alert">
            {submitError}
          </p>
        )}

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button className="min-h-[44px] w-full" disabled={!canSubmit}>
              {t('guard.incident.submitButton')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('guard.incident.confirmTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('guard.incident.confirmDescription')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="min-h-[44px]">{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction className="min-h-[44px]" onClick={handleSubmit}>
                {submitMutation.isPending && <RefreshCw className="mr-2 size-4 animate-spin" />}
                {t('common.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
